import { LocalDate } from "@js-joda/core";
import React, { useState } from "react";
import {
  DraftPatientContractDiagnosis,
  Icd10Codes,
  PatientContractDiagnosisCode,
  PatientContractDiagnosisCodeType,
  PatientContractDiagnosisSetParams,
} from "../../messages";
import { Icd10Code } from "../../messages/IcdCode";
import { PatientContractDiagnosisCodeId } from "../../messages/ObjectIds";
import { STRINGS } from "../../strings";
import { translate } from "../../translate";
import { fmap } from "../../utils/fmap";
import { undefinedToNull } from "../../utils/undefinedToNull";
import useDebounce from "../../utils/useDebounce";
import { withDefault } from "../../utils/withDefault";
import ErrorBottomSheet from "../ErrorBottomSheet/ErrorBottomSheet";
import { isDefined } from "../Generic/IsDefined";
import { openDatepicker } from "../MedflytDatepicker";
import PromptBottomSheet from "../PromptBottomSheet/PromptBottomSheet";
import ICDCard from "./components/ICDCard/ICDCard";
import SearchICDCard from "./components/SearchICDCard/SearchICDCard";
import { useSearchIcdCodes } from "./hooks/useSearchIcdCodes";

const PROMPT_ACTIONS = [
  { value: false, label: translate(STRINGS.NO) },
  { value: true, label: translate(STRINGS.YES) },
];

interface Props {
  title: string;
  type: PatientContractDiagnosisCodeType | null;
  diagnosis: PatientContractDiagnosisCode | null;
  isDisabled: boolean;
  isProfileSync: boolean;
  searchIcd: (searchValue: string) => Promise<Icd10Codes>;
  onCreate: (diagnosis: PatientContractDiagnosisSetParams, isProfileSync: boolean) => Promise<void>;
  onUpdate: (
    id: PatientContractDiagnosisCodeId,
    diagnosis: PatientContractDiagnosisSetParams,
    isProfileSync: boolean
  ) => Promise<void>;
  onDelete: (id: PatientContractDiagnosisCodeId, isProfileSync: boolean) => void;
  onClickProfileSync: (code: PatientContractDiagnosisCode | null) => void;
}

const SinglePatientContractDiagnosisCodeActivity = ({
  title,
  type,
  diagnosis,
  isDisabled,
  isProfileSync,
  searchIcd,
  onCreate,
  onUpdate,
  onDelete,
  onClickProfileSync,
}: Props) => {
  const [draft, setDraft] = useState<DraftPatientContractDiagnosis>();
  // const [isProfileSync, setProfileSync] = useState(true);
  const [localIsProfileSync, setLocalProfileSync] = useState(true);

  const [isSearchOpen, setSearchOpen] = useState(false);
  const [isDeletePromptOpen, setDeletePromptOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [isLoading, setLoading] = useState(false);
  const debounceSearchValue = useDebounce(searchValue, 500);
  const { results: searchResults, error: searchError } = useSearchIcdCodes(
    searchIcd,
    debounceSearchValue
  );
  const [formError, setFormError] = useState<string>();

  const setIcdCodeInDraft = React.useCallback(
    (icdCode: Icd10Code) => {
      setDraft({
        startDate: null,
        endDate: null,
        type: type,
        ...diagnosis,
        ...draft,
        icdCode: icdCode.id,
        formattedDxCode: icdCode.formattedDxCode,
        shortDesc: icdCode.shortDesc,
      });
    },
    [type, diagnosis, draft]
  );

  const calculateIsProfileSync = React.useCallback(() => {
    return isDefined(diagnosis) ? isProfileSync : localIsProfileSync;
  }, [diagnosis, isProfileSync, localIsProfileSync]);

  const handleClickCode = React.useCallback(() => {
    if (isDisabled) {
      return;
    }

    if (fmap(diagnosis, (x) => x.isForBilling) === true) {
      setFormError(translate(STRINGS.YOU_ARE_NOT_ALLOWED_TO_EDIT_THIS_FIELD));
      return;
    }

    setSearchOpen(true);
  }, [diagnosis, isDisabled]);

  const handleClickDate = React.useCallback(
    (defaultDate: LocalDate | null, callback: (newDate: LocalDate | undefined) => void) => {
      openDatepicker(
        {
          defaultDate: defaultDate !== null ? defaultDate.atStartOfDay() : undefined,
          type: "date",
          callbackObj: {
            callback: callback,
            cancelable: true,
          },
        },
        translate(STRINGS.SELECT_DATE)
      );
    },
    []
  );

  const handleClickStartDate = React.useCallback(() => {
    if (isDisabled) {
      return;
    }

    const defaultValue = fmap(diagnosis, (x) => x.startDate);

    if (fmap(diagnosis, (x) => x.isForBilling) === true) {
      setFormError(translate(STRINGS.YOU_ARE_NOT_ALLOWED_TO_EDIT_THIS_FIELD));
      return;
    }

    handleClickDate(defaultValue, (newDate) => {
      setDraft({
        icdCode: null,
        endDate: null,
        type: type,
        ...diagnosis,
        ...draft,
        startDate: undefinedToNull(newDate),
      });
    });
  }, [isDisabled, diagnosis, handleClickDate, type, draft]);

  const handleClickEndDate = React.useCallback(() => {
    if (isDisabled) {
      return;
    }

    const defaultValue = fmap(diagnosis, (x) => x.endDate);

    if (fmap(diagnosis, (x) => x.isForBilling) === true) {
      setFormError(translate(STRINGS.YOU_ARE_NOT_ALLOWED_TO_EDIT_THIS_FIELD));
      return;
    }

    handleClickDate(defaultValue, (newDate) => {
      setDraft({
        icdCode: null,
        startDate: null,
        type: null,
        ...diagnosis,
        ...draft,
        endDate: undefinedToNull(newDate),
      });
    });
  }, [diagnosis, draft, handleClickDate, isDisabled]);

  const handleClickSave = React.useCallback(() => {
    if (draft === undefined) {
      return;
    }

    const icdCode = withDefault(draft.icdCode, diagnosis !== null ? diagnosis.icdCode : null);

    if (icdCode === null) {
      setFormError(translate(STRINGS.YOU_HAVE_TO_PICK_A_CODE));
      return;
    }

    const diagnosisParams: PatientContractDiagnosisSetParams = {
      icdCode: icdCode,
      formattedDxCode: draft.formattedDxCode,
      shortDesc: draft.shortDesc,
      startDate: withDefault(
        draft.startDate,
        fmap(diagnosis, (x) => x.startDate)
      ),
      endDate: withDefault(
        draft.endDate,
        fmap(diagnosis, (x) => x.endDate)
      ),
      type: withDefault(
        draft.type,
        fmap(diagnosis, (x) => x.type)
      ),
    };

    setLoading(true);

    const promise =
      diagnosis === null
        ? onCreate(diagnosisParams, calculateIsProfileSync())
        : onUpdate(diagnosis.id, diagnosisParams, calculateIsProfileSync());

    promise
      .then(() => setDraft(undefined))
      .catch(() => setFormError(translate(STRINGS.SOMETHING_WENT_WRONG)))
      .then(() => setLoading(false));
  }, [diagnosis, draft, calculateIsProfileSync, onCreate, onUpdate]);

  const handleClickDelete = React.useCallback(() => setDeletePromptOpen(true), []);
  const handleClickCancel = React.useCallback(() => setDraft(undefined), []);
  const handleClickProfileSync = React.useCallback(
    (code) => {
      return isDefined(diagnosis)
        ? onClickProfileSync(code)
        : setLocalProfileSync(!localIsProfileSync);
    },
    [diagnosis, localIsProfileSync, onClickProfileSync]
  );

  const handleDismissPromptBottomSheet = React.useCallback(() => setDeletePromptOpen(false), []);
  const handleChoosePromptBottomSheet = React.useCallback(
    (value: any) => {
      setDeletePromptOpen(false);
      if (diagnosis !== null && value === true) {
        onDelete(diagnosis.id, calculateIsProfileSync());
      }
    },
    [diagnosis, calculateIsProfileSync, onDelete]
  );

  const handleRequestCloseErrorBottomSheet = React.useCallback(() => setFormError(undefined), []);

  const handleSelectIcdCode = React.useCallback(
    (icdCode: Icd10Code) => {
      setIcdCodeInDraft(icdCode);
      setSearchOpen(false);
    },
    [setIcdCodeInDraft]
  );

  const handleRequestCloseSearchICDCard = React.useCallback(() => setSearchOpen(false), []);

  return (
    <>
      <ICDCard
        title={title}
        diagnosis={diagnosis}
        draft={undefinedToNull(draft)}
        isProfileSync={calculateIsProfileSync()}
        isDisabled={isDisabled} // currently it'll always be enabled
        isLoading={isLoading}
        onClickCode={handleClickCode}
        onClickStartDate={handleClickStartDate}
        onClickEndDate={handleClickEndDate}
        onClickSave={handleClickSave}
        onClickDelete={handleClickDelete}
        onClickCancel={handleClickCancel}
        onClickProfileSync={handleClickProfileSync}
      />
      <ErrorBottomSheet
        isOpen={formError !== undefined}
        error={formError}
        onRequestClose={handleRequestCloseErrorBottomSheet}
      />
      <PromptBottomSheet
        title={translate(STRINGS.WARNING)}
        message={translate(STRINGS.ARE_YOU_SURE_YOU_WANT_TO_DELETE) + "?"}
        options={PROMPT_ACTIONS}
        onRequestClose={handleDismissPromptBottomSheet}
        isOpen={isDeletePromptOpen}
        onChoose={handleChoosePromptBottomSheet}
      />
      <SearchICDCard
        placeholder={`${translate(STRINGS.SEARCH)} ${title}`}
        value={searchValue}
        hasError={searchError !== undefined}
        isOpen={isSearchOpen}
        icdCodes={fmap(searchResults, (results) => results.codes)}
        onChange={setSearchValue}
        onSelectIcdCode={handleSelectIcdCode}
        onRequestClose={handleRequestCloseSearchICDCard}
      />
    </>
  );
};

export default SinglePatientContractDiagnosisCodeActivity;
